import { Button, PageHeader, returnToPrevious } from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { categoriesQuery, keys, tenantApi } from '../api/tenant';
import { FormStatus, mergeErrors } from '../components/fields';
import { ProductForm } from '../components/ProductForm';
import { he } from '../i18n/he';
import { productCreateRequest, productDraft, type ProductDraft } from '../lib/drafts';
import { NO_ERRORS, type FieldErrors } from '../lib/forms';

/** New statuses a product may start in (ARCHIVED is only for existing products). */
const CREATE_STATUSES = ['DRAFT', 'ACTIVE', 'HIDDEN'] as const;

/** A product the store sells that is not in the catalog. */
export function ProductCreatePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const categories = useQuery(categoriesQuery());
  const [draft, setDraft] = useState<ProductDraft>(() => productDraft());
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);

  const create = useMutation({
    mutationFn: tenantApi.createProduct,
    onSuccess: async (product) => {
      queryClient.setQueryData(keys.product(product.id), product);
      await queryClient.invalidateQueries({ queryKey: keys.products() });
      await navigate({ to: '/products/$productId', params: { productId: product.id } });
    },
  });

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = productCreateRequest(draft);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    create.mutate(parsed.value);
  };

  return (
    <>
      <Link
        to="/products"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.products.back}
      </Link>
      <PageHeader title={he.products.newTitle} />
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-6"
        data-testid="product-form"
      >
        <ProductForm
          draft={draft}
          set={(change) => {
            setDraft((current) => ({ ...current, ...change }));
          }}
          errors={mergeErrors(localErrors, create.error)}
          locked={false}
          categories={categories.data?.items ?? []}
          statuses={CREATE_STATUSES}
        />
        <FormStatus error={create.error} localInvalid={Object.keys(localErrors).length > 0} />
        <div>
          <Button type="submit" busy={create.isPending}>
            {he.common.create}
          </Button>
        </div>
      </form>
    </>
  );
}
